import {
  IconCode,
  IconFile,
  IconFileTypePdf,
  IconFileZip,
  IconPhoto,
} from "@tabler/icons-react";
import type { CategoriaDocumento, DocumentoVinculado } from "@/lib/types";

/**
 * Helpers puros para la presentación de documentos vinculados. Sin
 * React state · sin hooks · trivialmente testables.
 *
 * El único motivo por el que este archivo es `.tsx` y no `.ts` es
 * `nodoIconoPorExtension`, que devuelve un nodo JSX según la extensión
 * para que los rows lo dibujen sin tener que conocer el set de iconos.
 */

export interface GrupoDocumentos {
  categoria: string;
  items: DocumentoVinculado[];
}

/**
 * Orden estable de categorías · facturas primero, evidencias y
 * contratos al final. Las categorías futuras que no estén en el orden
 * caen al final (índice 99) y se mantienen entre sí en orden de
 * aparición.
 */
const ORDEN_CATEGORIAS: CategoriaDocumento[] = [
  "FacturaXml",
  "FacturaPdf",
  "Adicional",
  "Evidencia",
  "Contrato",
];

export function agruparPorCategoria(
  docs: DocumentoVinculado[]
): GrupoDocumentos[] {
  const map = new Map<string, DocumentoVinculado[]>();
  for (const d of docs) {
    const list = map.get(d.categoria) ?? [];
    list.push(d);
    map.set(d.categoria, list);
  }
  const presentes = Array.from(map.keys());
  presentes.sort((a, b) => {
    const ia = ORDEN_CATEGORIAS.indexOf(a as CategoriaDocumento);
    const ib = ORDEN_CATEGORIAS.indexOf(b as CategoriaDocumento);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  return presentes.map((c) => ({
    categoria: c,
    items: (map.get(c) ?? [])
      .slice()
      .sort((a, b) => a.nombreOriginal.localeCompare(b.nombreOriginal)),
  }));
}

export function labelCategoria(c: string): string {
  switch (c) {
    case "FacturaXml":
      return "Factura XML";
    case "FacturaPdf":
      return "Factura PDF";
    case "Adicional":
      return "Documentación Adicional";
    case "Evidencia":
      return "Evidencia";
    case "Contrato":
      return "Contrato";
    default:
      return c;
  }
}

export function badgeColorCategoria(c: string): string {
  switch (c) {
    case "FacturaXml":
      return "blue";
    case "FacturaPdf":
      return "red";
    case "Adicional":
      return "violet";
    case "Evidencia":
      return "teal";
    case "Contrato":
      return "orange";
    default:
      return "gray";
  }
}

export function extensionDe(nombre: string): string {
  const idx = nombre.lastIndexOf(".");
  if (idx < 0) return "";
  return nombre.slice(idx + 1).toLowerCase();
}

export function esImagen(ext: string): boolean {
  return ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext);
}

export function esPrevisualizable(ext: string): boolean {
  return (
    ext === "pdf" ||
    esImagen(ext) ||
    ext === "xml" ||
    ext === "json" ||
    ext === "csv" ||
    ext === "txt"
  );
}

/**
 * Devuelve un nodo de icono apropiado para la extensión. PDFs, imágenes,
 * archivos de código y zips tienen iconos distintivos · el resto cae al
 * icono genérico de archivo.
 */
export function nodoIconoPorExtension(ext: string, size = 16, stroke = 1.6) {
  if (ext === "pdf") return <IconFileTypePdf size={size} stroke={stroke} />;
  if (esImagen(ext)) return <IconPhoto size={size} stroke={stroke} />;
  if (ext === "xml" || ext === "json" || ext === "csv")
    return <IconCode size={size} stroke={stroke} />;
  if (ext === "zip" || ext === "rar" || ext === "7z")
    return <IconFileZip size={size} stroke={stroke} />;
  return <IconFile size={size} stroke={stroke} />;
}
